import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EditInstallmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    installment: {
        id: string;
        amount: number;
        due_date: string;
        notes?: string | null;
        currency: string;
    } | null;
}

const EditInstallmentModal: React.FC<EditInstallmentModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    installment
}) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        amount: '',
        due_date: '',
        notes: ''
    });

    useEffect(() => {
        if (installment) {
            setFormData({
                amount: installment.amount.toString(),
                due_date: installment.due_date.split('T')[0],
                notes: installment.notes || ''
            });
        }
    }, [installment]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!installment) return;

        setLoading(true);
        try {
            const { error } = await supabase
                .from('installments')
                .update({
                    amount: parseFloat(formData.amount),
                    due_date: formData.due_date,
                    notes: formData.notes || null
                })
                .eq('id', installment.id);

            if (error) throw error;

            alert('Taksit başarıyla güncellendi');
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error updating installment:', err);
            alert('Güncelleme sırasında hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !installment) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Taksit Düzenle
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tutar ({installment.currency})
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            required
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Vade Tarihi
                        </label>
                        <input
                            type="date"
                            required
                            value={formData.due_date}
                            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Notlar
                        </label>
                        <textarea
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                        >
                            İptal
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditInstallmentModal;
